/** French formatting. Precision adapts to magnitude: showing "0,00 €" for a task
 *  that costs a fifth of a cent tells the reader nothing, and showing four
 *  decimals on a 300 € total pretends to a precision nothing supports.
 *
 *  Currency lives here and nowhere else. Everything measured is in dollars, and
 *  everything computed stays in dollars; the reader's currency is a property of
 *  the reader, so it is applied at the last possible moment. Module-level state
 *  is deliberate: one page, one reader, one currency at a time — threading it
 *  through every call site would add noise without adding correctness.
 */
import { EUR_PER_USD } from '../engine/config'
import { getLang, t } from './lang'

export type Currency = 'EUR' | 'USD'

/** La locale des nombres suit la langue lue, pas la devise : un anglophone qui regarde
 *  des euros écrit quand même 1,234.5 et non 1 234,5. */
function locale(): string {
  return getLang() === 'fr' ? 'fr-FR' : 'en-US'
}

let currency: Currency = 'EUR'

export function setCurrency(next: Currency): void {
  currency = next
}

export function getCurrency(): Currency {
  return currency
}

/** Said on the page without the rate itself: the figure belongs to the method, the fact
 *  belongs to the reader — a price in euros that came from a dollar rate card. */
export function currencyRateNote(): string {
  return currency === 'EUR'
    ? t('Les montants sont convertis en euros à un taux fixe.',
      'Amounts are converted to euros at a fixed rate.')
    : t('Les montants sont en dollars, sans conversion.',
      'Amounts are in dollars, unconverted.')
}

/** Amounts are given to this function in USD, always. */
export function money(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—'
  const shown = currency === 'EUR' ? value * EUR_PER_USD : value
  const digits = decimalsFor(shown)
  return `${shown.toLocaleString(locale(), {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })} ${currency === 'EUR' ? '€' : '$'}`
}

/**
 * Le prix d'un forfait : celui que l'éditeur affiche dans la devise du lecteur quand il en
 * publie un, sinon la conversion. Un abonnement n'est pas un montant qu'on convertit — c'est
 * un tarif affiché, et OpenAI vend 8 € ce que la conversion de ses 8 $ met à 7,36 €.
 */
export function planMoney(usd: number, eur: number | undefined): string {
  if (currency === 'EUR' && eur !== undefined) {
    // Un tarif d'abonnement est un nombre rond : « 90,00 € » à côté de « 180 € » donnait
    // deux écritures pour la même chose.
    const digits = Number.isInteger(eur) ? 0 : decimalsFor(eur)
    return `${eur.toLocaleString(locale(), {
      minimumFractionDigits: digits, maximumFractionDigits: digits,
    })} €`
  }
  return money(usd)
}

/** A rate the reader typed, in their own currency, back to the dollars the
 *  engine works in. */
export function toUsd(shown: number): number {
  return currency === 'EUR' ? shown / EUR_PER_USD : shown
}

/** And the other way, to put their own number back in the field. */
export function fromUsd(usdValue: number): number {
  return currency === 'EUR' ? usdValue * EUR_PER_USD : usdValue
}

export function currencySymbol(): string {
  return currency === 'EUR' ? '€' : '$'
}

function decimalsFor(value: number): number {
  const magnitude = Math.abs(value)
  if (magnitude === 0) return 2
  if (magnitude < 0.01) return 4
  if (magnitude < 1) return 3
  if (magnitude < 100) return 2
  return 0
}

export function count(value: number | null | undefined, digits = 0): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—'
  return value.toLocaleString(locale(), {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })
}

/**
 * A token count, at the precision a reader can hold.
 *
 * The exact figure is noise — 24 312 887 says nothing that "24 M" does not — and
 * printing it whole invites a precision the estimate does not have.
 */
export function tokens(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—'
  // Spelled out, not abbreviated: this sentence is read by someone who has never
  // counted a token, and "2 900 M" asks them to do arithmetic to reach "about
  // three billion". Two significant figures, never more — "404,1 M" claims a
  // precision the estimate does not have.
  const round = { maximumSignificantDigits: 2 } as const
  //  Arrondi à deux chiffres, 999 millions devient « 1 000 millions » : c'est un milliard,
  //  et l'écrire autrement fait buter la lecture.
  if (value >= 995e6) {
    const count_ = value / 1e9
    return `${count_.toLocaleString(locale(), round)} `
      + t(count_ < 1.05 ? 'milliard' : 'milliards', 'billion')
  }
  if (value >= 1e6) {
    return `${(value / 1e6).toLocaleString(locale(), round)} ${t('millions', 'million')}`
  }
  return value.toLocaleString(locale(), { maximumFractionDigits: 0 })
}

export function factor(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—'
  // Trois régimes, parce qu'un multiplicateur ne se lit pas pareil selon sa taille :
  //  - sous 1, tout l'intérêt est après la virgule (×0,09 dit « onze fois moins cher ») ;
  //  - entre 1 et 10, la décimale pèse encore un cinquième du chiffre ;
  //  - au-delà, elle n'ajoute rien qu'un humain retienne — ×572,1 se lit ×572
  //    (Armand, 21/09/2026).
  const format = value < 1 ? { maximumSignificantDigits: 2 }
    : value < 10 ? { maximumFractionDigits: 1 }
      : { maximumFractionDigits: 0 }
  return `×${value.toLocaleString(locale(), format)}`
}

export function percent(value: number | null | undefined, digits = 0): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—'
  return `${value.toLocaleString(locale(), {
    minimumFractionDigits: digits, maximumFractionDigits: digits,
  })} %`
}

/** Durations read as time, not as a token count: 554 s means nothing at a glance. */
export function duration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return '—'
  if (seconds < 60) return `${Math.round(seconds)} s`
  const minutes = seconds / 60
  if (minutes < 60) {
    const whole = Math.floor(minutes)
    const rest = Math.round(seconds - whole * 60)
    return rest > 0 ? `${whole} min ${rest} s` : `${whole} min`
  }
  const hours = Math.floor(minutes / 60)
  const rest = Math.round(minutes - hours * 60)
  const hour = t('h', hours > 1 ? 'hrs' : 'hr')
  return rest > 0 ? `${hours} ${hour} ${rest} min` : `${hours} ${hour}`
}

export function minutes(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—'
  return duration(value * 60)
}

export function isoDay(value: string | null | undefined): string {
  if (!value) return '—'
  const day = value.slice(0, 10)
  const parts = day.split('-')
  if (parts.length !== 3) return day
  return `${parts[2]}/${parts[1]}/${parts[0]}`
}

const NATURE_LABELS: Record<string, string> = {
  question: 'question', explorer: 'exploration', coder: 'écriture de code',
  corriger: 'correction', verifier: 'vérification visuelle',
  deployer: 'déploiement', orchestrer: 'orchestration',
  automatiser: 'automatisation',
}

const SIZE_LABELS: Record<string, string> = {
  micro: 'micro', petite: 'petite', grosse: 'grosse',
}

const PLATFORM_LABELS: Record<string, string> = {
  web: 'web', ios: 'iOS', serveur: 'serveur', autre: 'plateforme non précisée',
}

export const label = {
  nature: (id: string): string => NATURE_LABELS[id] ?? id,
  size: (id: string): string => SIZE_LABELS[id] ?? id,
  platform: (id: string): string => PLATFORM_LABELS[id] ?? id,
}

export const PRECISION_TEXT: Record<string, { badge: string; className: string; note: string }> = {
  lecture_sure: {
    badge: 'tâche identifiée',
    className: 'tight',
    note: "Nature, plateforme et taille ont toutes été lues explicitement dans "
        + "votre phrase. La largeur de la fourchette, elle, n'est pas un choix : "
        + "c'est la dispersion mesurée de l'archétype retenu.",
  },
  lecture_incertaine: {
    badge: 'lecture partielle',
    className: 'wide',
    note: "J'ai dû deviner au moins un élément (la taille, la plateforme). Vérifiez "
        + '« ce que j’ai compris » plus bas — et regardez l’estimation alternative : '
        + "si je me suis trompé d'archétype, c'est elle le bon chiffre.",
  },
  ordre_de_grandeur: {
    badge: 'ordre de grandeur',
    className: 'rough',
    note: "Une partie de la description n'a pas été reconnue, ou l'archétype retenu "
        + 'est trop dispersé pour qu’un chiffre veuille dire quelque chose. Ordre de '
        + 'grandeur, pas budget.',
  },
}
